import React, {useEffect, useState} from "react";
import CurrencyFormat from "react-currency-format";
import { getSupplierTransactions } from "../../../actions/suppliers_transaction";
import {useDispatch, useSelector} from "react-redux";
import TransactionDeleteModal from "./TransactionDeleteModal";
import {Pagination} from "react-bootstrap";
import useQueryParams from "../../../utils/hooks/useQueryParams";
import {getParams} from "../../../utils/hooks/getParams";
import {useLocation} from "react-router-dom";
import { usePermission } from '../../../hooks/usePermission';

const SupplierTranscations = ({supplier}) => {
    const dispatch = useDispatch();
    const supplier_transactions = useSelector(state => state.suppliers_transaction.supplier_transactions);
    const {numberOfPages, current_page} = useSelector(state => state.suppliers_transaction);
    const [deleteModal, setDeleteModal] = useState({ show: false, transaction: null });
    const location = useLocation();
    const queryParams = useQueryParams();
    const { userCan } = usePermission();

    const {id} = supplier;
    const [currentPage, setCurrentPage] = useState(parseInt(queryParams.page) || 1);
    const [filters, setFilters] = useState(() => {
        const params = getParams(true);
        return {
            from_date: params.from_date || '',
            to_date: params.to_date || '',
            page: parseInt(params.page) || 1
        };
    });

    useEffect(() => {
        if (id) {
            dispatch(getSupplierTransactions(id, {...filters, page: currentPage}));
        }
    }, [dispatch, id, currentPage]);

    // Update state when URL changes
    useEffect(() => {
        const params = getParams(true);
        if (params.page && parseInt(params.page) !== currentPage) {
            setCurrentPage(parseInt(params.page));
        }
        
        // Update filters from URL
        const newFilters = {...filters};
        let changed = false;
        
        if (params.from_date !== filters.from_date) {
            newFilters.from_date = params.from_date || '';
            changed = true;
        }
        
        if (params.to_date !== filters.to_date) {
            newFilters.to_date = params.to_date || '';
            changed = true;
        }
        
        if (changed) {
            setFilters(newFilters);
        }
    }, [location.search]);

    const [from_date, setFromDate] = useState(filters.from_date || '');
    const [to_date, setToDate] = useState(filters.to_date || '');

    const onChangeFromDate = (event) => {
        const value = event.target.value;
        setFromDate(value);
        
        // Update URL
        const url = new URL(window.location);
        if (value) {
            url.searchParams.set('from_date', value);
        } else {
            url.searchParams.delete('from_date');
        }
        url.searchParams.set('page', 1);
        window.history.pushState({}, '', url);
        
        const newFilters = {
            ...filters,
            from_date: value,
            page: 1
        };
        setFilters(newFilters);
        setCurrentPage(1);
        
        dispatch(getSupplierTransactions(supplier.id, newFilters));
    };

    const onChangeToDate = (event) => {
        const value = event.target.value;
        setToDate(value);
        
        // Update URL
        const url = new URL(window.location);
        if (value) {
            url.searchParams.set('to_date', value);
        } else {
            url.searchParams.delete('to_date');
        }
        url.searchParams.set('page', 1);
        window.history.pushState({}, '', url);
        
        const newFilters = {
            ...filters,
            to_date: value,
            page: 1
        };
        setFilters(newFilters);
        setCurrentPage(1);
        
        dispatch(getSupplierTransactions(supplier.id, newFilters));
    };

    const onPageChange = (page) => {
        setCurrentPage(page);
        
        // Update URL
        const url = new URL(window.location);
        url.searchParams.set('page', page);
        window.history.pushState({}, '', url);
    };
    
    const renderPagination = () => {
        let items = [];
        const totalPages = numberOfPages || 1;
        
        if (totalPages <= 1) return null;

        items.push(
            <Pagination.Prev 
                key="prev" 
                onClick={() => onPageChange(Math.max(1, currentPage - 1))} 
                disabled={currentPage === 1}
            />
        );
        
        // Add first page
        items.push(
            <Pagination.Item
                key={1}
                active={1 === currentPage}
                onClick={() => onPageChange(1)}
            >
                1
            </Pagination.Item>
        );

        // Add ellipsis if needed
        if (currentPage > 3) {
            items.push(<Pagination.Ellipsis key="ellipsis1"/>);
        }

        // Add pages around current page
        for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
            if (i <= 1 || i >= totalPages) continue;
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => onPageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        // Add ellipsis if needed
        if (currentPage < totalPages - 2) {
            items.push(<Pagination.Ellipsis key="ellipsis2"/>);
        }

        // Add last page
        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage}
                    onClick={() => onPageChange(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }
        
        items.push(
            <Pagination.Next 
                key="next" 
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages} 
            />
        );

        return items;
    };

    const handleShowDeleteModal = (transaction) => {
        setDeleteModal({ show: true, transaction });
    };

    const handleCloseDeleteModal = () => {
        setDeleteModal({ show: false, transaction: null });
    };

    const getCurrentFilters = () => ({
        ...filters,
        page: currentPage
    });

    return (
        <div className="w-100 table-responsive px-2">
            <div className="mb-2 align-items-center d-flex justify-content-between">
                <div className="mb-3">
                    <label htmlFor="date1" className="mb-2">dan</label>
                    <input onChange={onChangeFromDate} className="form-control"
                           value={from_date}
                           id="date1" type="date"/>
                </div>

                <div className="mb-3">
                    <label htmlFor="date2" className="mb-2">gacha</label>
                    <input onChange={onChangeToDate} className="form-control"
                           value={to_date}
                           id="date2" type="date"/>
                </div>
            </div>

            <table className="table custom-table table-hover">
                <thead className="table-dark">
                <tr>
                    <th scope="col">#</th>
                    <th scope="col">Olingan summa</th>
                    <th scope="col">To'langan summa</th>
                    <th scope="col">Sana</th>
                    <th scope="col">Amallar</th>
                </tr>
                </thead>
                <tbody>
                {
                    supplier_transactions?.map((item, index) => (
                        <tr key={index}>
                            <th scope="row">{index + 1}</th>
                            <th>
                                <CurrencyFormat value={item.received_amount}
                                                displayType={'text'}
                                                thousandSeparator={true}
                                                suffix={"  so'm"}/>
                            </th>
                            <th>
                                <CurrencyFormat value={item.paid_amount}
                                                displayType={'text'}
                                                thousandSeparator={true}
                                                suffix={"  so'm"}/>
                            </th>
                            <th>{item.date}</th>
                            <th>
                                {userCan('delete supplier transaction') && (
                                    <button 
                                        onClick={() => handleShowDeleteModal(item)}
                                        className="btn btn-danger btn-sm">
                                        O'chirish
                                    </button>
                                )}
                            </th>
                        </tr>
                    ))
                }
                </tbody>
            </table>
            
            {/* Pagination controls */}
            <div className="d-flex justify-content-center mt-3">
                <Pagination>
                    {renderPagination()}
                </Pagination>
            </div>

            <TransactionDeleteModal
                show={deleteModal.show}
                onHide={handleCloseDeleteModal}
                transaction={deleteModal.transaction}
                supplierId={supplier.id}
                filters={getCurrentFilters()}
            />
        </div>
    )
}

export default SupplierTranscations;